<template>
  <v-form
    ref="formRef"
    class="mx-auto"
    style="max-width: 600px"
    @submit.prevent="onSubmit"
  >
    <v-text-field
      v-model="user.email"
      :readonly="inEditMode"
      :disabled="isSubmitting"
      :rules="[validationRules.required, validationRules.email]"
      :prepend-inner-icon="inEditMode ? mdiLockOutline : undefined"
      label="Email"
      name="email"
      variant="outlined"
      density="default"
    />

    <v-row align="center">
      <v-col cols="12" :sm="inEditMode ? 8 : 12">
        <v-select
          v-model="user.role_id"
          :disabled="isSubmitting"
          :loading="rolesLoading"
          :items="roles"
          :rules="[validationRules.required]"
          label="Perfil"
          item-title="description"
          item-value="id"
          name="role"
          variant="outlined"
          density="default"
        />
      </v-col>
      <v-col v-if="inEditMode" cols="12" sm="4">
        <v-switch
          v-model="user.is_active"
          :label="user.is_active ? 'Ativo' : 'Desativado'"
          :color="user.is_active ? 'success' : undefined"
          :disabled="isLoading"
          inset
          hide-details
        />
      </v-col>
    </v-row>

    <div class="d-flex ga-2 mt-2">
      <v-btn
        v-if="inEditMode"
        color="error"
        variant="tonal"
        :disabled="isSubmitting || isLoading"
        @click="onDeleteGrant"
      >
        Excluir
      </v-btn>
      <v-spacer />
      <v-btn
        variant="text"
        :disabled="isLoading || isSubmitting"
        @click="emit('close')"
      >
        Cancelar
      </v-btn>
      <v-btn
        type="submit"
        color="primary"
        :disabled="isLoading"
        :loading="isSubmitting"
      >
        Salvar
      </v-btn>
    </div>

    <v-dialog
      v-model="confirmDeleteDialog"
      width="480"
      :persistent="isDeleting"
    >
      <v-card>
        <v-card-text class="pt-6 text-center">
          <v-avatar size="56" color="error" variant="tonal" class="mb-4">
            <v-icon size="32" :icon="mdiAlertCircleOutline" />
          </v-avatar>
          <div class="text-h6 mb-2">Confirmação de exclusão</div>
          <div class="text-body-2 text-medium-emphasis">
            Tem certeza que deseja excluir este acesso? Esta ação não pode ser
            desfeita.
          </div>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer />
          <v-btn
            variant="text"
            :disabled="isDeleting"
            @click="confirmDeleteDialog = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="error"
            :loading="isDeleting"
            @click="onConfirmDeleteGrant"
          >
            Excluir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-form>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { mdiAlertCircleOutline, mdiLockOutline } from "@mdi/js";
import { validationRules } from "@/utils/validation";
import { useRolesQuery } from "@/queries/modules/roles";
import { useUserQuery } from "@/queries/modules/users";
import {
  useCreateGrantMutation,
  useUpdateGrantMutation,
  useDeleteGrantMutation,
} from "@/queries/modules/grants";
import { useSnackbar } from "@/plugins/snackbar";

const props = defineProps({
  userId: {
    type: [Number, String],
    required: false,
  },
});

const emit = defineEmits(["close", "saved", "on-grant-deleted"]);

const $snackbar = useSnackbar();

// State
const formRef = ref(null);
const confirmDeleteDialog = ref(false);
const grant = ref(null);
const user = ref({
  email: "",
  role_id: null,
  is_active: false,
});

// Computed
const inEditMode = computed(() => !!props.userId);

// TanStack Query for roles
const rolesParams = computed(() => ({ type: "vendor", limit: 10000 }));
const { data: rolesData, isLoading: rolesLoading } = useRolesQuery(rolesParams);
const roles = computed(() => rolesData.value?.items || []);

// TanStack Query for user (in edit mode)
const userId = computed(() => (props.userId ? Number(props.userId) : null));
const { data: userData, isLoading: userLoading } = useUserQuery(userId, {
  role_type: "vendor",
});

// Watch for user data changes
watch(
  userData,
  (newUserData) => {
    if (newUserData && inEditMode.value) {
      user.value.email = newUserData.email;
      if (newUserData.grants && newUserData.grants[0]) {
        grant.value = newUserData.grants[0];
        user.value.role_id = grant.value.role?.id;
        user.value.is_active = grant.value.is_active;
      }
    }
  },
  { immediate: true },
);

// Mutations
const createGrantMutation = useCreateGrantMutation();
const updateGrantMutation = useUpdateGrantMutation();
const deleteGrantMutation = useDeleteGrantMutation();

// Computed loading states
const isLoading = computed(() => rolesLoading.value || userLoading.value);
const isSubmitting = computed(
  () =>
    createGrantMutation.isPending.value || updateGrantMutation.isPending.value,
);
const isDeleting = computed(() => deleteGrantMutation.isPending.value);

// Methods
const onDeleteGrant = () => {
  confirmDeleteDialog.value = true;
};

const onConfirmDeleteGrant = async () => {
  try {
    await deleteGrantMutation.mutateAsync(grant.value.id);
    confirmDeleteDialog.value = false;
    emit("on-grant-deleted", grant.value);
  } catch (e) {
    $snackbar.error(e.message);
  }
};

const onSubmit = async () => {
  const { valid } = await formRef.value.validate();
  if (!valid) {
    return;
  }

  try {
    if (inEditMode.value) {
      await updateGrantMutation.mutateAsync({
        grantId: grant.value.id,
        data: {
          role_id: user.value.role_id,
          is_active: user.value.is_active,
          company_group_id: null,
        },
      });
    } else {
      await createGrantMutation.mutateAsync({
        email: user.value.email,
        role_id: user.value.role_id,
        company_group_id: null,
        tenant_id: null,
      });
    }
    emit("saved");
  } catch (e) {
    $snackbar.error(e.message);
  }
};
</script>
